import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { useAuth } from './useAuth'
import { tryAssumeAdminRole } from './adminCreds'
import { AdminContext, type AdminState } from './AdminContext'
import { refreshDelayMs } from './refreshSchedule'

function randomSessionName(): string {
  // Avoid leaking any admin-identifying substring into CloudTrail RoleSessionName.
  const uuid = crypto.randomUUID().replace(/-/g, '').slice(0, 16)
  return `admin-${uuid}`
}

export function AdminProvider({ children }: { children: ReactNode }) {
  const { state: authState } = useAuth()
  const [state, setState] = useState<AdminState>({ status: 'loading' })

  const idToken = authState.status === 'authenticated' ? authState.idToken : null

  const probe = useCallback(async () => {
    if (!idToken) return
    const region = import.meta.env.VITE_AWS_REGION
    try {
      const creds = await tryAssumeAdminRole(idToken, region, randomSessionName())
      setState(creds ? { status: 'admin', credentials: creds } : { status: 'not-admin' })
    } catch (err) {
      console.error('[admin] probe failed unexpectedly', err)
      setState({ status: 'not-admin' })
    }
  }, [idToken])

  useEffect(() => {
    if (authState.status === 'loading') {
      setState({ status: 'loading' })
      return
    }
    if (!idToken) {
      setState({ status: 'not-admin' })
      return
    }
    // Re-probe on idToken change (hourly silent renewal included), but keep
    // existing admin credentials while the probe is in flight: the STS creds
    // remain valid on their own schedule, and dropping to 'loading' would flip
    // isAdmin false for the probe's duration — silently un-impersonating and
    // pointing user-scoped reads/writes at the admin's own prefix mid-flight.
    setState((prev) => (prev.status === 'admin' ? prev : { status: 'loading' }))
    void probe()
  }, [authState.status, idToken, probe])

  // Schedule a silent refresh a few minutes before the admin creds expire.
  useEffect(() => {
    if (state.status !== 'admin') return
    const delay = refreshDelayMs(state.credentials.expiration.getTime())
    const timer = setTimeout(() => {
      void probe()
    }, delay)
    return () => clearTimeout(timer)
  }, [state, probe])

  return (
    <AdminContext.Provider value={{ state, refresh: probe }}>{children}</AdminContext.Provider>
  )
}
